import { Intersection, Object3D, Vector2, Raycaster, Camera } from "three";
import { ndc } from "../view3d";
import { registerListener } from "../html/register-listener";

/**
 * The Picker tries to give the three.js Raycaster class a simpler interface.
 */
export class Picker {

    canvas: HTMLCanvasElement;
    camera: Camera;
    intersections: Intersection<Object3D>[] = [];
    pos: Vector2 = new Vector2();
    raycaster: Raycaster = new Raycaster();
    watched: Set<Object3D> = new Set();


    // The latest mouse position, made available since we're already watching
    // the pointer movement.
    clientX: number;
    clientY: number;

    // When we updated the watched set, we update this array
    private watchedArray: Array<Object3D> = [];

    constructor(camera: Camera, canvas: HTMLCanvasElement) {
        this.camera = camera;
        this.canvas = canvas;

        this.raycaster.layers.enableAll();
    }

    enable() {
        registerListener(window, 'pointerdown', this.update);
        registerListener(window, 'pointermove', this.update);
    }

    disable() {
        removeEventListener('pointerdown', this.update);
        removeEventListener('pointermove', this.update);
    }

    /**
     * Select a subset of the scene to watch.
     */
    watch(...objs: Object3D[]) {
        for(let obj of objs) {
            //log.debug('ManualPicker.watch adding', obj);
            this.watched.add(obj);
        }
        this.watchedArray = Array.from(this.watched);
    }

    unwatch(...objs: Object3D[]) {
        for(let obj of objs) {
            //log.debug('ManualPicker.watch removing', obj);
            this.watched.delete(obj);
        }
        this.watchedArray = Array.from(this.watched);
    }

    unwatchAll() {
        this.watched = new Set();
        this.watchedArray.length = 0;
    }

    /**
     * Most often one is probably just interested in the object that was hit
     * that is closest to the camera. If any object was hit, then this will be
     * the one closest to the camera. If no object was hit, then this will be
     * undefined (falsy).
     */
    get topHit(): Object3D | undefined {
        this.intersections.length = 0;
        this.raycaster.intersectObjects(
            this.watchedArray, false, this.intersections);
        if(this.intersections.length > 0) {
            return this.intersections[0].object;
        } else {
            return undefined;
        }
    }

    /**
     * Updates the raycaster state from pointer events on the window.
     */
    private update = (event: MouseEvent | DragEvent) => {
        this.clientX = event.clientX;
        this.clientY = event.clientY;

        const rect = this.canvas.getBoundingClientRect();
        if(event.clientX > rect.right) return;
        if(event.clientY < rect.top) return;
        if(event.clientY > rect.bottom) return;
        if(event.clientX < rect.left) return;

        const
            x = event.clientX - rect.left,
            y = event.clientY - rect.top;

        ndc(x, y, rect.width, rect.height, this.pos);
        this.raycaster.setFromCamera(this.pos, this.camera);
    }

}
export default Picker;
